import { Box, LinearProgress, Paper, Typography } from "@mui/material";
import { MenuBookOutlined, PlayArrowRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import type { AcademyCourse } from "./types";
import { PrimaryButton, StatusPill, learnerStatus, useAcademyColors } from "./AcademyUi";

export default function CourseCard({ course }: { course: AcademyCourse }) {
  const navigate = useNavigate();
  const { colors, isDarkMode } = useAcademyColors();
  const status = learnerStatus(course);
  return (
    <Paper
      elevation={0}
      sx={{
        overflow: "hidden", height: "100%", display: "flex", flexDirection: "column", borderRadius: "12px",
        backgroundColor: isDarkMode ? colors.primary[900] : "#fff", border: `1px solid ${colors.primary[300]}30`,
        transition: "all 0.3s ease",
        "&:hover": { transform: "translateY(-4px)", boxShadow: "0 8px 16px rgba(97,68,56,0.15)" },
      }}
    >
      <Box
        sx={{
          height: 150, position: "relative", display: "flex", alignItems: "center", justifyContent: "center",
          backgroundColor: `${colors.primary[400]}15`,
          backgroundImage: course.image ? `url(${course.image})` : "none",
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        {!course.image && <MenuBookOutlined sx={{ fontSize: 48, color: `${colors.primary[400]}80` }} />}
        <Box sx={{ position: "absolute", top: 12, right: 12 }}><StatusPill status={status} /></Box>
      </Box>
      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.25, flexGrow: 1 }}>
        {course.category && (
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: colors.primary[400], textTransform: "uppercase", letterSpacing: "0.5px" }}>
            {course.category}
          </Typography>
        )}
        <Typography sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.3 }}>{course.title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{
          flexGrow: 1, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {course.description}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {course.module_count} modules · {course.lesson_count} lessons
        </Typography>
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">Progress</Typography>
            <Typography variant="caption" fontWeight={600}>{course.progress}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={course.progress}
            sx={{ height: 6, borderRadius: 3, backgroundColor: `${colors.primary[400]}20`,
              "& .MuiLinearProgress-bar": { backgroundColor: "#614438", borderRadius: 3 } }}
          />
        </Box>
        <PrimaryButton startIcon={<PlayArrowRounded />} onClick={() => navigate(`/academy/courses/${course.id}`)}>
          {course.completed ? "Review course" : status === "in_progress" ? "Continue" : "Start course"}
        </PrimaryButton>
      </Box>
    </Paper>
  );
}
